import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  CalendarMonth as CalendarMonthIcon,
  Event as EventIcon,
  Email as EmailIcon,
  MailOutline as MailOutlineIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  TextFields as TextFieldsIcon,
  Settings as SettingsIcon,
  Create as CreateIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Folder as FolderIcon,
  HelpOutline as HelpIcon,
  Webhook as WebhookIcon,
  AutoAwesome as AIIcon,
  AccountTree as WorkflowIcon,
  Psychology as PsychologyIcon,
  Archive as ArchiveIcon,
  SmartToy as ChatBotIcon,
  Campaign as CampaignIcon,
  Schedule as ScheduleIcon,
  Apps as AppsIcon,
  EventNote as FristIcon,
  Computer as ComputerIcon,
  Timer as TimerIcon,
  Receipt as ReceiptIcon,
  CloudUpload as PortalDocsIcon,
  Chat as PortalMsgIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RagChatWidget from '../components/RagChatWidget';

const drawerWidth = 240;

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'AI Chat', icon: <ChatBotIcon />, path: '/ai-chat' },
    { text: 'Tickets', icon: <AssignmentIcon />, path: '/tickets' },
    { text: 'Mein Kalender', icon: <CalendarMonthIcon />, path: '/mein-kalender' },
    { text: 'Workflows', icon: <WorkflowIcon />, path: '/workflows' },
    { text: 'E-Mails', icon: <EmailIcon />, path: '/emails' },
    { text: 'Adressen', icon: <PeopleIcon />, path: '/adressen' },
    { text: 'Kunden', icon: <BusinessIcon />, path: '/kunden' },
    { text: 'Fristen', icon: <FristIcon />, path: '/fristen' },
    { text: 'Zeiterfassung', icon: <TimerIcon />, path: '/zeiterfassung' },
    { text: 'Abrechnung', icon: <ReceiptIcon />, path: '/abrechnung' },
    { text: 'Dokumente', icon: <DescriptionIcon />, path: '/dokumente' },
    { text: 'Portal-Dokumente', icon: <PortalDocsIcon />, path: '/portal-dokumente' },
    { text: 'Portal-Nachrichten', icon: <PortalMsgIcon />, path: '/portal-nachrichten' },
    { text: 'Paperless', icon: <ArchiveIcon />, path: '/paperless' },
    { text: 'Projekte', icon: <FolderIcon />, path: '/projects' },
    { text: 'Vorlagen', icon: <CreateIcon />, path: '/vorlagen' },
    { text: 'Textbausteine', icon: <TextFieldsIcon />, path: '/textbausteine' },
    { text: 'Wissensdatenbank', icon: <PsychologyIcon />, path: '/wissensdatenbank' },
    { text: 'Assets', icon: <ComputerIcon />, path: '/assets' },
    { text: 'Quick Links', icon: <AppsIcon />, path: '/quicklinks' },
    { text: 'Meine Einstellungen', icon: <AccountCircleIcon />, path: '/benutzer-einstellungen' },
  ];

  const adminMenuItems = [
    { text: 'System-Einstellungen', icon: <SettingsIcon />, path: '/einstellungen' },
    { text: 'Mail-Konten', icon: <MailOutlineIcon />, path: '/mail-konten' },
    { text: 'Geplante Jobs', icon: <ScheduleIcon />, path: '/admin/jobs' },
    { text: 'Webhook-Verwaltung', icon: <WebhookIcon />, path: '/webhooks' },
    { text: 'KI System-Prompts', icon: <AIIcon />, path: '/system-prompts' },
    { text: 'Mitteilungen', icon: <CampaignIcon />, path: '/admin/mitteilungen' },
    { text: 'Mandanten', icon: <BusinessIcon />, path: '/admin/mandanten' },
    { text: 'Benutzer', icon: <AdminPanelSettingsIcon />, path: '/admin/benutzer' },
  ];

  // Check if user has admin role
  const isAdmin = user?.rollen?.some(
    (rolle) => rolle.rolleSchluessel === 'admin'
  ) ?? false;

  const drawer = (
    <div>
      <Toolbar sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
        <img
          src="/logo.svg"
          alt="Skeena Logo"
          style={{ height: '40px', maxWidth: '90%', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        />
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {isAdmin && (
        <>
          <Divider />
          <List subheader={
            <ListItem>
              <Typography variant="overline" color="text.secondary">
                Administration
              </Typography>
            </ListItem>
          }>
            {adminMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileOpen(false);
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Skeena - KI-gestützte Brief & Mail Verwaltung
          </Typography>
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                color="inherit"
                onClick={() => navigate('/hilfe')}
                title="Hilfe"
              >
                <HelpIcon />
              </IconButton>
              <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {user.anzeigeName || user.benutzername}
              </Typography>
              <IconButton
                onClick={handleUserMenuOpen}
                color="inherit"
                sx={{ p: 0.5 }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {(user.vorname?.[0] || user.benutzername[0]).toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem disabled>
                  <ListItemIcon>
                    <AccountCircleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={user.vollstaendigerName || user.benutzername}
                    secondary={user.email}
                  />
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Abmelden" />
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>

      {/* RAG Chat Widget */}
      <RagChatWidget />
    </Box>
  );
};

export default MainLayout;
